import { useEffect, useState } from 'react';
import { RPCSchemas } from '@bits/rpc';
import { useToast } from '@bits/ui';
import { CubeFocus, List, Signpost } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { useFilteredList } from '@/lib/advanced-filters/use-filtered-list';
import { fetchAuditTrail } from '@/components/audit-trail/use-audit-trail';

const PAGE_SIZE = 25;

export const useFilteredAuditTrail = (resourceId?: string) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const filteredList = useFilteredList({
    savedFiltersKey: 'audit-trail',
    defaultFilters: {},
    fetchFacets: () =>
      fetchAuditTrail(resourceId!, {
        limit: 1,
        offset: 0,
      }).then(({ facets }) => ({ facets: facets ?? [] })),
    facetOptionTransforms: {
      eventType: {
        label: 'Event type',
        icon: Signpost,
      },
      resourceType: {
        label: 'Resource type',
        icon: CubeFocus,
      },
    },
  });

  const request = useSWR(
    filteredList.ready &&
      resourceId && [
        'audit-trail',
        resourceId,
        filteredList.page,
        filteredList.sortBy,
        filteredList.search,
        PAGE_SIZE,
        filteredList.formattedRequestFilters,
      ],
    () =>
      fetchAuditTrail(resourceId ?? '', {
        limit: PAGE_SIZE,
        offset: (filteredList.page - 1) * PAGE_SIZE,
        filters: filteredList.formattedRequestFilters,
        sorting: [
          {
            field: 'publishedAt',
            order: 'DESCENDING',
          },
        ],
      }),
    {
      shouldRetryOnError: false,
      onError: () => {
        toast({
          variant: 'error',
          title: t('errors:item-list.title'),
          description: t('errors:item-list.description', { type: 'cases' }),
        });
      },
    }
  );

  const [results, setResults] = useState<{
    auditLogs: RPCSchemas['AuditLog'][];
    total: number;
  }>({
    auditLogs: [],
    total: 0,
  });

  useEffect(() => {
    if (request.data?.auditLogs) {
      setResults({
        auditLogs: request.data.auditLogs,
        total: request.data.total,
      });
    }
  }, [request.data]);

  return {
    filteredList,
    request,
    results,
    pageSize: PAGE_SIZE,
  };
};
